import { useEffect, useMemo, useRef, useState } from 'react';
import { MESSAGE_PRESETS, type ShowMessage } from '@micplot/shared';
import { api } from '../lib/api';
import { useStore, useMessages } from '../state/store';

/**
 * Short notes between positions.
 *
 * A1 and A2 are on headsets but often cannot talk -- the band is loud, a mic is
 * open, or the note is for two minutes from now. Presets are one tap because
 * during a changeover nobody is typing.
 */
export default function Messages({ onClose }: { onClose: () => void }) {
  const identity = useStore(s => s.identity)!;
  const notify = useStore(s => s.notify);
  const messages = useMessages();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function send(body: string, urgent = false) {
    const clean = body.trim();
    if (!clean || sending) return;
    setSending(true);
    try {
      await api.sendMessage(identity.code, clean, urgent);
      setText('');
    } catch (err) {
      notify((err as Error).message, 'error');
    } finally {
      setSending(false);
    }
  }

  return (
    <aside className="msg-panel" aria-label="Messages">
      <div className="panel-head">
        <span className="panel-title">Messages</span>
        <span className="count">{messages.length}</span>
        <div className="header-spacer" />
        <button className="btn sm" onClick={onClose} aria-label="Close messages">✕</button>
      </div>

      <div className="msg-log">
        {messages.length === 0 ? (
          <div className="empty" style={{ padding: 20 }}>
            <div style={{ fontSize: 13 }}>Nothing yet.</div>
            <div style={{ fontSize: 12 }}>Tap a preset below to send one.</div>
          </div>
        ) : (
          messages.map(m => <Bubble key={m.id} m={m} code={identity.code} notify={notify} />)
        )}
        <div ref={endRef} />
      </div>

      <div className="msg-presets">
        {MESSAGE_PRESETS.map(p => (
          <button key={p.text}
                  className={`btn sm${p.urgent ? ' danger' : ''}`}
                  disabled={sending}
                  onClick={() => send(p.text, p.urgent)}>
            {p.text}
          </button>
        ))}
      </div>

      <form className="msg-compose"
            onSubmit={e => { e.preventDefault(); void send(text); }}>
        <input value={text} onChange={e => setText(e.target.value)}
               placeholder="Type a note…" maxLength={280} aria-label="Message" />
        <button className="btn primary" disabled={!text.trim() || sending}>Send</button>
      </form>
    </aside>
  );
}

function Bubble({ m, code, notify }: {
  m: ShowMessage; code: string; notify: (t: string, k?: 'info' | 'error') => void;
}) {
  const identity = useStore(s => s.identity)!;
  const mine = m.participantId === identity.participantId;
  const time = useMemo(() => new Date(m.createdAt).toLocaleTimeString([], {
    hour: '2-digit', minute: '2-digit',
  }), [m.createdAt]);

  return (
    <div className={`msg${mine ? ' mine' : ''}${m.urgent ? ' urgent' : ''}`}>
      <div className="msg-meta">
        <strong>{m.authorName}</strong>
        <span>{time}</span>
      </div>
      <div className="msg-text">{m.text}</div>
      {m.ackAt ? (
        <div className="msg-ack">✓ seen by {m.ackBy}</div>
      ) : !mine ? (
        <button className="btn sm" style={{ marginTop: 6 }}
                onClick={async () => {
                  try { await api.ackMessage(code, m.id); }
                  catch (err) { notify((err as Error).message, 'error'); }
                }}>
          Acknowledge
        </button>
      ) : null}
    </div>
  );
}
